import React from "react";
import {
    BiddingView,
    BidViewItem,
    NameViewMode,
    AmountViewMode,
    BidderViewMode,
} from "../bid/bidding_model";
import {USDollar, USDollarCompact} from "../../api/utils";
import {Link, useParams} from "react-router-dom";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "../ui/card";
import {Badge} from "../ui/badge";
import {moneyFormatter} from "../../lib/formatters";
import {Eligibility, RoundModel} from "../project_conf/round_model";
import {Button} from "../ui/button";
import BaseLoadingSpinner from "../ui/baseLoadingSpinner";


type BidFeedbackProps = {
    bidViewData: BiddingView;
}

export function getOrdinal(n: number) {
    if (n % 10 == 1 && n % 100 != 11) {
        return 'st';
    } else if (n % 10 == 2 && n % 100 != 12) {
        return 'nd';
    } else if (n % 10 == 3 && n % 100 != 13) {
        return 'rd';
    }
    return 'th';
}


/**
 * todo -
 * */
function describeNumberOfBidders(num_bidders: number) {
    return (
        <div className="flex items-center space-x-2">
            <span>There are </span>
            <span className="text-lg font-semibold">{num_bidders}</span>
            <span>active bidders on the project</span>
        </div>
    );
}

function Row({item}: { item: BidViewItem }) {

    if (!item.yours) {
        return (
            <div className="flex justify-between border-b py-1 ">
                <span className="text-sm">{
                    typeof item.amount === "number"
                        ? moneyFormatter.format(item.amount)
                        : item.amount
                }</span>
                <span className="text-sm">{item.name}</span>
            </div>
        );
    } else {
        return (
            <div className="flex justify-between border-b py-1 bg-blue-100 rounded-md space-x-20">
                <span className="font-semibold text-md">{
                    typeof item.amount === "number"
                        ? moneyFormatter.format(item.amount)
                        : item.amount
                }</span>
                <span className="font-semibold text-md">{item.name}</span>
            </div>
        );
    }
}

/**
 * These are the 2 modes of the card
 * 1) LineItemCard
 * 2) TextCard
 * */
function LineItemCard({bidViewData}: BidFeedbackProps) {
    return (
        <div className="space-y-2">
            {bidViewData?.items?.map((item, index) =>
                <Row key={index} item={item}/>)}

            {bidViewData?.names ? (
                <div className="text-sm text-gray-600">
                    <div className="font-medium">The other bidders are:</div>
                    <div className="space-x-2 space-y-1">
                        {bidViewData.names.map((name) => (
                            <Badge key={name}>{name}</Badge>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}


function TextCard({bidViewData}: BidFeedbackProps) {
    return (
        <div className="space-y-4">
            <div>
                Your Latest Bid:{" "}
                <Link
                    to={`/project/${bidViewData.latest?.project_id}/bid/${bidViewData.latest?.id}`}
                >
                    <span className="text-lg font-semibold">
                        {moneyFormatter.format(
                            bidViewData?.latest?.bid_amount
                        )}
                    </span>
                </Link>
            </div>

            {describeNumberOfBidders(bidViewData?.num_bidders)}

            {bidViewData?.ranking ? (
                <div className="text-lg font-semibold">
                    Your Bid is ranked {bidViewData.ranking} {getOrdinal(bidViewData.ranking)} out of {" "}
                    {bidViewData.num_bidders}
                </div>
            ) : null}

            {bidViewData?.names ? (
                <div>
                    <div className="text-sm text-gray-600 font-medium">
                        The other bidders are:
                    </div>
                    <div className="space-x-2 space-y-1">
                        {bidViewData.names.map((name) => (
                            <Badge key={name}>{name}</Badge>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

export function makeCard(bidViewData: BiddingView) {
    /**
     * See BidConfig.jpg for the logic
     * essentially there are 2 modes - 'line-item-y' or 'text-card-y'
     *
     * Only the data that is available to a bidder is returned.
     * */
    if (!bidViewData.latest) {
        return <div>Submit a bid to see where you rank.</div>;
    } else if (bidViewData?.mode.amounts === AmountViewMode.Visible) {
        return <LineItemCard bidViewData={bidViewData}/>;
    } else if (bidViewData?.mode.amounts === AmountViewMode.Ranked) {
        if (bidViewData?.mode.names === NameViewMode.Visible) {
            return <LineItemCard bidViewData={bidViewData}/>;
        } else {
            return <TextCard bidViewData={bidViewData}/>;
        }
    } else {
        return <TextCard bidViewData={bidViewData}/>;
    }
}

/**
 * Generate fake data based for a card so a developer can see how bids will display.
 *                  amounts
 *              vv | vr | vh    v-lmh
 * names        hv | hr | hh    h-lmh
 *              dv | dr | dh    d-lmh
 *
 * names: visible, hidden, detached
 * amounts: vuisible, ranked, hidden, low-medium,high
 *
 * */
export function SampleData(mode: BidderViewMode) {
    const res: BiddingView = {
        mode: mode,
        num_bidders: 5,
        ranking: null,
        items: null,
        names: null,
        latest: {
            id: "",
            project_id: "",
            company_id: "",
            title: "sample",
            bid_amount: 40000000,
        },
    };
    let items = [
        {name: "Builder 1", amount: 39000000, yours: false},
        {name: "Builder 2", amount: 41000000, yours: false},
        {name: "Builder 3", amount: 42000000, yours: true},
        {name: "Builder 4", amount: 43000000, yours: false},
        {name: "Builder 5", amount: 44000000, yours: false},
    ];

    // (vv, hv, dv) -> line-item-y
    if (mode.amounts === AmountViewMode.Visible) {
        if (mode.names === NameViewMode.Visible) {
            res.items = items;
        } else {
            res.items = items.map((x) =>
                x.yours ? x : {...x, ...{name: "redacted"}}
            );
        }
    }

    // (vr, hr, dr) -> add the ranking
    if (mode.amounts === AmountViewMode.Ranked) {
        res.ranking = 3;

        // (vr) -> special case
        if (mode.names === NameViewMode.Visible) {
            res.items = items.map((x) =>
                x.yours ? x : {...x, ...{amount: "redacted"}}
            );
        }
    }

    // (dv, dr, dh, vh) -> attach the list of competitors
    if (
        mode.names === NameViewMode.Detached ||
        (mode.names === NameViewMode.Visible &&
            mode.amounts === AmountViewMode.Hidden)
    ) {
        res.names = items.filter((x) => !x["yours"]).map((x) => x["name"]);
    }
    return res;
}


type FeedbackProps = {
    bidViewData: BiddingView | undefined
    round: RoundModel
}


/**
 *
 * */
const AuctionStatusView = ({bidViewData, round}: FeedbackProps) => {
    const {project_id} = useParams();

    if (!bidViewData) {
        return <div><BaseLoadingSpinner/></div>;
    }
    return (
        <Card>
            <CardHeader>
                <div className="flex justify-between items-center">
                    <CardTitle>Bidding Summary</CardTitle>
                    {/*<Button variant="outline">*/}
                    {/*            /!*<Link*!/*/}
                    {/*             to={`/project/${project_id}/edit`}*!/*/}
                    {/*            /!*></Link>*!/*/}
                    {/*    More Info*/}
                    {/*</Button>*/}
                </div>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
                <ul className="space-y-2">
                    {/*{round.name*/}
                    {/*    ? */}
                    <li>
                        <span>Current Round: {round.name} </span>
                        {round.end_date
                            ? <span>will last until
                                    <strong> {round.end_date.split("T")[0]}</strong>
                                  </span>
                            : <span>is open indefinitely</span>
                        }
                    </li>
                    <li>
                        <strong>{round.eligibility === Eligibility.Closed
                            ? "The Project is not accepting Bids at this time"
                            : "The project is accepting Bids"}
                        </strong>
                    </li>
                    <li>
                        <span>Competing bidder amounts are: </span>
                        <span className="font-medium">
                            {bidViewData.mode.amounts}
                        </span>
                    </li>
                    <li>
                        <span>Bidder names are: </span>
                        <span className="font-medium">
                            {bidViewData.mode.names}
                        </span>
                    </li>
                </ul>
                {makeCard(bidViewData)}
            </CardContent>
        </Card>
    );
};

export default AuctionStatusView;
